import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import "./Maintenance.css"

/* Whether this device has a network at all.
 *
 * `navigator.onLine` is famously only trustworthy in one direction: `true` means "there is an
 * interface up", which is not the same as "the internet works". `false`, though, is reliable —
 * and false is the direction that matters here, because it is the one that tells us the server is
 * probably fine and the player is on a train. */
const useOnline = () => {
  const [online, setOnline] = useState(() => (typeof navigator === "undefined" ? true : navigator.onLine !== false))

  useEffect(() => {
    const goOnline = () => setOnline(true)
    const goOffline = () => setOnline(false)
    window.addEventListener("online", goOnline)
    window.addEventListener("offline", goOffline)
    return () => {
      window.removeEventListener("online", goOnline)
      window.removeEventListener("offline", goOffline)
    }
  }, [])

  return online
}

/* Shown when the bootstrap could not reach the backend.
 *
 * There are two ways to get here and they had one sentence between them. That was fine while this
 * was a page in a browser tab, where a player has an address bar and a refresh button and their
 * own sense of whether their wifi is up. Installed to a home screen it is not: the app launches
 * with no browser chrome at all, so "the site is under maintenance" is the only thing it says, and
 * on a train it is a lie about somebody else's server.
 *
 * So an offline launch says so, and reloads itself when the connection comes back — the bootstrap
 * runs once at start-up, and there is nothing else here for the player to press. */
const Maintenance = () => {
  const { t } = useTranslation()
  const online = useOnline()

  useEffect(() => {
    // Only after having been offline: this component mounts online whenever the server is the
    // thing that is down, and reloading into the same failure would be a loop.
    if (online) return undefined
    const onBack = () => window.location.reload()
    window.addEventListener("online", onBack)
    return () => window.removeEventListener("online", onBack)
  }, [online])

  return (
    <div className="maintenance">
      <h1>{online ? t("app.maintenanceTitle") : t("app.offlineTitle")}</h1>
      <p>{online ? t("app.maintenanceBody") : t("app.offlineBody")}</p>
      {online ? (
        <button type="button" onClick={() => window.location.reload()}>
          {t("app.retry")}
        </button>
      ) : null}
    </div>
  )
}

export default Maintenance
